/**
 * Page Order (task 071-01) — Chelsie's 2026-09-28 ask: "how can I see which
 * sequences and spots each page are in without going into each campaign?"
 *
 * The pages in the walk, in the order visitors see them. The api
 * (`GET /admin/page-order`) sends the rows in walk order per sequence
 * (epic 077 D10), each page's 7-day responses, revenue and revenue per
 * response, the sequences each audience walks, and the walk's master switch.
 * Since HF-146 round 2 (operator 2026-10-29) the screen is ONE order
 * section — "Order by <revenue|value|spot>", the rows of the sequence(s)
 * the audiences walk; pages parked in a sequence nobody walks are not shown
 * — then the Completed card, then Not placed, then the holdout readout.
 * This screen only groups and labels the rows: it never re-sorts, except to
 * hand the editor the fallback (spot) order. It has two edit controls:
 * Pin to #1 / Unpin per page (task 077-03), and task 071-03's Change order,
 * which swaps the section's table for the drag-and-drop editor
 * (`page-order-reorder.tsx`). Custom pages are placed automatically when
 * scheduled or started (epic 083); the campaign form only shows where a
 * page is.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  ApiError,
  type PageOrderArmsHistoryDay,
  type PageOrderAutoPinResponse,
  type PageOrderFirstShareRow,
  type PageOrderResponse,
  type PageOrderRow,
  type PageOrderValueEstimateRow,
  type PageOrderValueStats,
} from '../lib/api';
import { campaignStyleEnumToLabel, pageUrl } from '../lib/campaign-styles';
import { fmtCount, fmtDate, fmtDateTime, fmtMoney } from '../lib/format';
import { Banner } from '../components/ui/banner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { StatusPill } from '../components/ui/status-pill';
import { Tag } from '../components/ui/tag';
import { useToast } from '../components/ui/toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { SequenceReorder, isEndedPage } from './page-order-reorder';

type Group =
  | { kind: 'sequence'; sequence: number; rows: PageOrderRow[] }
  | { kind: 'not-placed'; rows: PageOrderRow[] };

const NULL_SPOT_HINT =
  'Reached only when no numbered page in this sequence is live, and the walk ends after it.';

const REVENUE_HELP =
  'This order seats new pages (under 100 responses) and is the order visitors see if revenue order is switched off. Pin a page to put it at #1.';

const SPOT_MODE_BANNER =
  'Revenue order is off (OFFERS_WALK_ORDER=spot): visitors see pages by spot number and the pin has no effect. The numbers show what the ranking would use.';

const VALUE_MODE_BANNER =
  "Value order is on (OFFERS_WALK_ORDER=value): the value arm learns each page's worth of a first slot and shows each visitor its own learned order, so visitors see different orders. The holdout arm keeps today's order — the list below shows the holdout's order and each page's learned numbers.";

/** Shared Lifewater caps make the two arms' tallies indicative, not a proof (epic 098 R3). */
const ARMS_CAVEAT =
  "Shared caps make this comparison indicative, not a proof: the arms share caps, so a day's revenue per visitor is not measured in isolation.";

function ascending(a: number, b: number): number {
  return a - b;
}

function compareIds(a: string, b: string): number {
  const idA = BigInt(a);
  const idB = BigInt(b);
  if (idA === idB) return 0;
  return idA < idB ? -1 : 1;
}

/**
 * 071's order: spot ascending, no spot last, then campaign id. In revenue
 * mode the API sends the walk order, so the editor is handed this one: it
 * seeds its draft from the order it is given, and saving the walk order back
 * as spots would silently rewrite them.
 */
function bySpot(a: PageOrderRow, b: PageOrderRow): number {
  if (a.spot !== b.spot) {
    if (a.spot === null) return 1;
    if (b.spot === null) return -1;
    return a.spot - b.spot;
  }
  return compareIds(a.campaignId, b.campaignId);
}

/** A 0..1 share as a whole percent, rounded to one decimal (`42%`, `2.5%`). */
export function formatSharePercent(share: number): string {
  const percent = share * 100;
  const rounded = Math.round(percent * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}

/**
 * The Estimate cell: `86% ± 4%` — the Beta mean as a percent, the sd as
 * percentage points, both one-decimal.
 */
export function formatEstimate(mean: number, sd: number): string {
  return `${formatSharePercent(mean)} ± ${formatSharePercent(sd)}`;
}

/** The Estimate cell's tooltip: the posterior the number comes from. */
export function estimateTooltip(row: PageOrderValueEstimateRow, estimatedOn: string): string {
  return (
    `Alpha ${row.alpha}, Beta ${row.beta}, estimated on ${estimatedOn}. ` +
    'The share of visitors the model expects to accept the page.'
  );
}

/** The First slots cell's tooltip: yesterday's value-arm walk counts behind the share. */
export function firstShareTooltip(row: PageOrderFirstShareRow): string {
  return (
    `Shown first in ${row.firstShows} of ${row.walks} value-arm walk(s) ` +
    `(${row.top3Shows} within the first 3 slots) yesterday.`
  );
}

/** The readout card's one arm-day cell: `1,234 · $0.41/visitor`. */
export function formatArmDay(day: PageOrderArmsHistoryDay['value']): string {
  if (day === null) return '—';
  const perVisitor = day.revenuePerVisitor === null ? '—' : fmtMoney(day.revenuePerVisitor);
  return `${fmtCount(day.visitors)} · ${perVisitor}/visitor`;
}

/** The readout arm-day cell's tooltip: the tally behind the two numbers. */
export function armDayTooltip(day: PageOrderArmsHistoryDay['value']): string | undefined {
  if (day === null) return undefined;
  const perVisitor =
    day.revenuePerVisitor === null
      ? 'no per-visitor revenue'
      : `$${day.revenuePerVisitor.toFixed(4)} per visitor`;
  return `${fmtCount(day.visitors)} visitors, ${fmtCount(day.billableAccepts)} accepts, ${fmtMoney(day.revenue)} revenue — ${perVisitor}.`;
}

/** The calendar date, `yyyy-MM-dd`, in the browser's zone. */
function localYmd(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The ranking is stale when its day is before yesterday (the browser's date; ET is close enough). */
function isStale(rankedOn: string): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return rankedOn < localYmd(yesterday);
}

/**
 * HF-146 r2 (operator 2026-10-29): ONE order section — the rows of the
 * sequence(s) the audiences actually walk, in the API's order — with the
 * Completed card second and Not placed after it. Rows parked in sequences
 * nobody walks («Остальные sequence не показываются») are not shown.
 */
function buildSections(data: PageOrderResponse): { order: Group; notPlaced: Group | null } {
  const walked = [...new Set([data.paidSocialSequence, data.ctoSequence])].sort(ascending);
  const rows = data.rows.filter((row) => row.sequence !== null && walked.includes(row.sequence));
  // The editor and the pin write to ONE sequence. Normally both audiences
  // walk the same sequence (the October config); when they split, the
  // section keys on the first walked sequence that holds rows, and the
  // editor is scoped to that sequence's rows only (the other walked
  // sequence's rows stay in the table, uneditable).
  const sequence = walked.find((seq) => rows.some((row) => row.sequence === seq)) ?? walked[0];
  const notPlaced = data.rows.filter((row) => row.sequence === null);
  return {
    order: { kind: 'sequence', sequence, rows },
    notPlaced: notPlaced.length > 0 ? { kind: 'not-placed', rows: notPlaced } : null,
  };
}

/** The order section's title: `Order by revenue|value|spot` (operator 2026-10-29). */
function orderTitle(walkOrder: PageOrderResponse['walkOrder']): string {
  return `Order by ${walkOrder === 'spot' ? 'spot' : walkOrder}`;
}

/** Who walks the order section's pages. */
function orderSectionLine(data: PageOrderResponse): string {
  if (data.paidSocialSequence === data.ctoSequence) {
    return 'Paid Social and CTO visitors walk these pages in the order below.';
  }
  return `Paid Social visitors walk Sequence ${data.paidSocialSequence} and CTO visitors walk Sequence ${data.ctoSequence}; the list shows both.`;
}

/**
 * Inline external-link icon (no icon library — task 021-09 ruling #6); copied
 * from campaigns.tsx (file-local precedent).
 */
const OpenPageIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-3.5 w-3.5"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

interface PageRowProps {
  row: PageOrderRow;
  first: boolean;
  walkOrder: PageOrderResponse['walkOrder'];
  /** Sequence groups carry the Pin column; Not placed rows have no pin. */
  inSequence: boolean;
  pinDisabled: boolean;
  /** Value mode only (epic 098 D4): the stats block the value columns read, null when the walk has no estimate run yet. */
  valueStats: PageOrderValueStats | null;
  /** Value mode on a placed-rows table: the two value columns render, with dashes for missing data. */
  valueMode: boolean;
  onPin(row: PageOrderRow): void;
  onUnpin(row: PageOrderRow): void;
}

function PageRow({
  row,
  first,
  walkOrder,
  inSequence,
  pinDisabled,
  valueStats,
  valueMode,
  onPin,
  onUnpin,
}: PageRowProps) {
  const nullSpotInSequence = row.sequence !== null && row.spot === null;
  const isNew =
    walkOrder === 'revenue' && row.spot !== null && (row.rank === null || !row.rank.ranked);
  const popOutUrl = pageUrl(row.pageSlug);
  // Epic 098 D4: the value columns read only the block's own rows — a page
  // outside the block (or the audience line) renders a dash. Not-placed rows
  // carry no stats by construction and never show the columns (not placed =
  // not in the walk).
  const estimate = valueStats?.rows.find((entry) => entry.campaignId === row.campaignId) ?? null;
  const firstShare =
    valueStats?.firstShare?.rows.find((entry) => entry.campaignId === row.campaignId) ?? null;
  // Epic 107 D1/D2: no hosted page or a CTO Email row has nothing to open, and
  // custom_optin pages open with the CTA switched off (preview=inert); other
  // styles open the page as-is.
  const popOutHref =
    popOutUrl === null || row.campaignStyle === 'cto_email'
      ? null
      : row.campaignStyle === 'custom_optin'
        ? `${popOutUrl}?preview=inert`
        : popOutUrl;
  return (
    <TableRow>
      <TableCell className="tabular-nums">{row.position ?? '—'}</TableCell>
      <TableCell className="tabular-nums">
        {row.spot !== null ? row.spot : nullSpotInSequence ? 'No spot' : '—'}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/campaigns/${row.campaignId}`}
            className="font-semibold text-ink hover:text-accent transition-colors"
          >
            {row.campaignName}
          </Link>
          {popOutHref !== null && (
            <button
              type="button"
              aria-label="Open page in new tab"
              title="Open page in new tab"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(popOutHref, '_blank', 'noopener');
              }}
              className="shrink-0 rounded-md p-1 transition bg-line/60 text-muted hover:bg-accent-soft hover:text-accent"
            >
              <OpenPageIcon />
            </button>
          )}
          {first && <Tag tone="accent">#1 today</Tag>}
          {row.pinned && <Tag tone="violet">Pinned</Tag>}
          {isNew && <Tag tone="sky">New: holds spot {row.spot}</Tag>}
          {row.sequence !== null && row.priceMissing && (
            <Tag tone="amber">No price: ranks as $0</Tag>
          )}
          {row.campaignStyle != null && row.campaignStyle !== 'custom_optin' && (
            <span className="text-xs text-muted-foreground">
              {campaignStyleEnumToLabel(row.campaignStyle)}
            </span>
          )}
          {row.archived && <Tag>Archived</Tag>}
        </div>
        {nullSpotInSequence && (
          <p className="mt-1 text-xs text-muted-foreground">{NULL_SPOT_HINT}</p>
        )}
      </TableCell>
      <TableCell>
        <StatusPill value={row.status} />
      </TableCell>
      <TableCell>{fmtDate(row.startDate) || '—'}</TableCell>
      <TableCell>{fmtDate(row.endDate) || '—'}</TableCell>
      <TableCell>{row.liveToday ? 'Yes' : 'No'}</TableCell>
      <TableCell className="tabular-nums">
        {row.rank === null ? '—' : fmtCount(row.rank.responses)}
      </TableCell>
      <TableCell className="tabular-nums">
        {row.rank === null ? '—' : fmtMoney(row.rank.revenue)}
      </TableCell>
      <TableCell className="tabular-nums">
        {row.rank === null || row.rank.revenuePerResponse === null ? (
          '—'
        ) : (
          <span title={`$${row.rank.revenuePerResponse.toFixed(4)}`}>
            {fmtMoney(row.rank.revenuePerResponse)}
          </span>
        )}
      </TableCell>
      {valueMode && (
        <>
          <TableCell className="tabular-nums">
            {firstShare === null ? (
              '—'
            ) : (
              <span title={firstShareTooltip(firstShare)}>
                {formatSharePercent(firstShare.share)}
              </span>
            )}
          </TableCell>
          <TableCell className="tabular-nums">
            {estimate === null || valueStats === null ? (
              '—'
            ) : (
              <span title={estimateTooltip(estimate, valueStats.estimatedOn)}>
                {formatEstimate(estimate.mean, estimate.sd)}
              </span>
            )}
          </TableCell>
        </>
      )}
      <TableCell>
        {inSequence && row.spot !== null && (
          <Button
            type="button"
            size="sm"
            disabled={pinDisabled}
            onClick={() => (row.pinned ? onUnpin(row) : onPin(row))}
          >
            {row.pinned ? 'Unpin' : 'Pin to #1'}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

interface ReadoutCardProps {
  valueStats: PageOrderValueStats;
}

/**
 * The value-mode readout (epic 098 D4, R3): the holdout comparison over the
 * last 14 run days — per arm, visitors and revenue per visitor — with the
 * shared-caps caveat visible. Newest day first, one row per day.
 */
function ReadoutCard({ valueStats }: ReadoutCardProps) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Holdout readout — value vs holdout arm</CardTitle>
        <p className="text-sm text-muted-foreground">
          Value-arm and holdout-arm visitors and revenue per visitor over the last{' '}
          {valueStats.armsHistory.length} run day(s), newest first. Estimated on{' '}
          {fmtDate(valueStats.estimatedOn)}.
        </p>
      </CardHeader>
      <CardContent>
        {valueStats.armsHistory.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No walk tallies yet — the comparison starts with the first nightly run.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead>Value arm</TableHead>
                <TableHead>Holdout arm</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {valueStats.armsHistory.map((entry) => (
                <TableRow key={entry.day}>
                  <TableCell>{fmtDate(entry.day)}</TableCell>
                  <TableCell className="tabular-nums" title={armDayTooltip(entry.value)}>
                    {formatArmDay(entry.value)}
                  </TableCell>
                  <TableCell className="tabular-nums" title={armDayTooltip(entry.holdout)}>
                    {formatArmDay(entry.holdout)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <p className="mt-3 text-xs text-muted-foreground">{ARMS_CAVEAT}</p>
      </CardContent>
    </Card>
  );
}

/**
 * The Auto pin list card (epic 115 D6): every scheduled Custom Opt-in with the
 * go-live auto-pin chosen, in the server's `scheduleDate`-ascending order.
 * These entries are PLANS, not the current pins — the current pin only changes
 * when a listed campaign actually goes live, and identical dates promise no
 * winner (the tie-break is rendering stability only). The list reads its own
 * `autoPins` array, independent of the sequence sections below.
 */
function AutoPinListCard({ autoPins }: { autoPins: PageOrderAutoPinResponse[] }) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Auto pin list</CardTitle>
        <p className="text-sm text-muted-foreground">
          Scheduled Custom Opt-in campaigns that will pin to #1 when they go live. These are
          upcoming choices, not the current pins: a page pins only when its campaign actually
          starts, replacing the sequence&apos;s pin then. Two entries with the same scheduled start
          do not promise which one wins.
        </p>
      </CardHeader>
      <CardContent>
        {autoPins.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No upcoming auto pins. Tick “Pin to #1 when this campaign goes live” while scheduling a
            Custom Opt-in campaign to add one.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Sequence</TableHead>
                <TableHead>Scheduled start (UTC)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {autoPins.map((entry) => (
                <TableRow key={entry.campaignId}>
                  <TableCell>
                    <Link
                      to={`/campaigns/${entry.campaignId}`}
                      className="font-semibold text-ink hover:text-accent transition-colors"
                    >
                      {entry.campaignName}
                    </Link>
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {entry.sequence !== null ? entry.sequence : 'Not placed'}
                  </TableCell>
                  {/* fmtDateTime renders the exact ISO UTC instant — the
                      activation gate reads this same scheduleDate; no browser
                      locale, no ET substitution. */}
                  <TableCell className="tabular-nums">{fmtDateTime(entry.scheduleDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

interface GroupCardProps {
  group: Group;
  data: PageOrderResponse;
  /** The sequence whose editor is open, if any (one card edits at a time). */
  editingSequence: number | null;
  /** A pin request is in flight: every pin button waits. */
  pinBusy: boolean;
  onPin(row: PageOrderRow): void;
  onUnpin(row: PageOrderRow): void;
  onEdit(sequence: number): void;
  onSaved(data: PageOrderResponse): void;
  onStale(): void;
  onCancel(): void;
}

function GroupCard({
  group,
  data,
  editingSequence,
  pinBusy,
  onPin,
  onUnpin,
  onEdit,
  onSaved,
  onStale,
  onCancel,
}: GroupCardProps) {
  // Epic 098 R1: the Change-order editor seeds from spot order in every mode
  // (revenue rows are walk-ordered, value rows are the holdout's order) — it
  // must never seed from, or save back, the walk order. So this switch keeps
  // its exact meaning — revenue mode and only revenue mode — and the editor
  // seed below extends the same spot-order fallback to value mode. Do not
  // widen this comparison to include 'value'.
  const revenueMode = data.walkOrder === 'revenue';
  const rows = group.rows;
  const isSequence = group.kind === 'sequence';
  // HF-146 r2: the editor saves ONE sequence's spots, so it is scoped to the
  // section's own sequence even when the walk splits across two sequences
  // and the table shows both (the split case is documented in buildSections).
  const ownRows = useMemo(
    () =>
      group.kind === 'sequence' ? rows.filter((row) => row.sequence === group.sequence) : rows,
    [group, rows]
  );
  // R3: in revenue mode the API rows are in walk order; epic 098 R1: in value
  // mode they are the holdout's walk order — either way the editor starts
  // from the fallback (spot) order. Spot mode's rows are already spot order.
  const editorRows = useMemo(
    () => (revenueMode || data.walkOrder === 'value' ? [...ownRows].sort(bySpot) : ownRows),
    [revenueMode, data.walkOrder, ownRows]
  );
  const editing = isSequence && group.sequence === editingSequence;
  const canReorder = isSequence && ownRows.some((row) => !isEndedPage(row));
  const liveRows = rows.filter((row) => row.liveToday);
  // The walk shows the first live row in API order (spot or not) first; Not
  // placed pages are never in a walk. With a split walk the first live row
  // of the merged list carries the tag.
  const firstLiveId = isSequence ? liveRows[0]?.campaignId : undefined;
  // Epic 098 D4: the value columns belong to the walk — a Not placed page is
  // not in it, so its table stays as-is (the block's rows are placed-only).
  const valueStats = isSequence ? data.valueStats : null;
  const valueMode = isSequence && data.walkOrder === 'value';
  const columns = valueMode ? 13 : 11;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>{isSequence ? orderTitle(data.walkOrder) : 'Not placed'}</CardTitle>
            {isSequence && (
              <p className="text-sm text-muted-foreground">{orderSectionLine(data)}</p>
            )}
            {!isSequence && (
              <p className="text-sm text-muted-foreground">
                Not in any walk. A custom page joins the end of the walk when you Schedule or Start
                it.
              </p>
            )}
            {isSequence && revenueMode && (
              <p className="text-sm text-muted-foreground">
                {data.ranking === null
                  ? 'Ordered by earnings once the first nightly ranking runs; until then pages show by spot.'
                  : `Ordered by earnings per response, refreshed nightly. Ranked on ${fmtDate(data.ranking.rankedOn)} from ${fmtDate(data.ranking.windowFrom)} to ${fmtDate(data.ranking.windowTo)}.`}
              </p>
            )}
          </div>
          {canReorder && !editing && (
            <Button
              type="button"
              size="sm"
              disabled={editingSequence !== null}
              onClick={() => onEdit(group.sequence)}
            >
              {revenueMode ? 'Change fallback order' : 'Change order'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isSequence && revenueMode && data.ranking !== null && isStale(data.ranking.rankedOn) && (
          <Banner tone="warn" className="mb-4">
            The nightly ranking hasn&apos;t run since {fmtDate(data.ranking.rankedOn)}, so this
            order uses older numbers.
          </Banner>
        )}
        {isSequence && liveRows.length === 0 && (
          <Banner tone="warn" className="mb-4">
            No page in the walk is live today, so its visitors see no custom page and go straight to
            the end of the walk.
          </Banner>
        )}
        {editing ? (
          <>
            {revenueMode && <p className="mb-3 text-sm text-muted-foreground">{REVENUE_HELP}</p>}
            <SequenceReorder
              sequence={group.sequence}
              rows={editorRows}
              onSaved={onSaved}
              onStale={onStale}
              onCancel={onCancel}
            />
          </>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Spot</TableHead>
                <TableHead>Page</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>End</TableHead>
                <TableHead>In the walk today</TableHead>
                <TableHead>Responses (7 d)</TableHead>
                <TableHead>Revenue (7 d)</TableHead>
                <TableHead>Revenue / response</TableHead>
                {valueMode && (
                  <>
                    <TableHead>First slots</TableHead>
                    <TableHead>Estimate</TableHead>
                  </>
                )}
                <TableHead>Pin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns} className="text-center text-muted-foreground py-8">
                    No pages in the walk yet.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <PageRow
                    key={row.campaignId}
                    row={row}
                    first={row.campaignId === firstLiveId}
                    walkOrder={data.walkOrder}
                    inSequence={isSequence}
                    pinDisabled={pinBusy || editingSequence !== null}
                    valueStats={valueStats}
                    valueMode={valueMode}
                    onPin={onPin}
                    onUnpin={onUnpin}
                  />
                ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

interface CompletedCardProps {
  rows: PageOrderRow[];
}

/**
 * HF-146: the Completed card — every not-yet-archived completed custom page,
 * one list (completed pages from every sequence, operator 2026-10-29). The
 * table keeps the base columns but shows no Sequence/Spot (a completed page
 * holds no spot: the server frees it the moment the campaign completes) and
 * no walk artifacts (#1 today, New, No price) — a completed page never
 * walks. No Change order, no Pin: those belong to sequences.
 */
function CompletedCard({ rows }: CompletedCardProps) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Completed</CardTitle>
        <p className="text-sm text-muted-foreground">
          Finished pages, out of every sequence. A page moves here the moment its campaign completes
          — end date, click limit or by hand — and its spot frees up right away. Archiving a page
          here hides it from this list.
        </p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Page</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Start</TableHead>
              <TableHead>End</TableHead>
              <TableHead>Responses (7 d)</TableHead>
              <TableHead>Revenue (7 d)</TableHead>
              <TableHead>Revenue / response</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No completed pages yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.campaignId}>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/campaigns/${row.campaignId}`}
                        className="font-semibold text-ink hover:text-accent transition-colors"
                      >
                        {row.campaignName}
                      </Link>
                      {row.campaignStyle != null && row.campaignStyle !== 'custom_optin' && (
                        <span className="text-xs text-muted-foreground">
                          {campaignStyleEnumToLabel(row.campaignStyle)}
                        </span>
                      )}
                      {row.archived && <Tag>Archived</Tag>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusPill value={row.status} />
                  </TableCell>
                  <TableCell>{fmtDate(row.startDate) || '—'}</TableCell>
                  <TableCell>{fmtDate(row.endDate) || '—'}</TableCell>
                  <TableCell className="tabular-nums">
                    {row.rank === null ? '—' : fmtCount(row.rank.responses)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {row.rank === null ? '—' : fmtMoney(row.rank.revenue)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {row.rank === null || row.rank.revenuePerResponse === null ? (
                      '—'
                    ) : (
                      <span title={`$${row.rank.revenuePerResponse.toFixed(4)}`}>
                        {fmtMoney(row.rank.revenuePerResponse)}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function PageOrder() {
  const [data, setData] = useState<PageOrderResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingSequence, setEditingSequence] = useState<number | null>(null);
  const [pinBusy, setPinBusy] = useState(false);
  const toast = useToast();

  const load = useCallback(() => {
    api
      .getPageOrder()
      .then(setData)
      .catch((err: ApiError) => setError(err.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function changePin(row: PageOrderRow, pin: boolean) {
    const { sequence } = row;
    if (data === null || sequence === null) return;
    setPinBusy(true);
    try {
      if (pin) {
        const replaced = data.rows.find(
          (other) =>
            other.sequence === sequence && other.pinned && other.campaignId !== row.campaignId
        );
        setData(await api.setPagePin(sequence, row.campaignId));
        toast.success(
          `Pinned "${row.campaignName}" to #1 in Sequence ${sequence}` +
            (replaced ? ` (replaces "${replaced.campaignName}")` : '')
        );
      } else {
        setData(await api.clearPagePin(sequence));
        toast.success(`Unpinned "${row.campaignName}"`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change the pin');
    } finally {
      setPinBusy(false);
    }
  }

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading page order: {error}
        </div>
      </div>
    );
  }

  const sections = data === null ? null : buildSections(data);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Page Order</h1>
        <p className="text-muted-foreground mt-2">
          The pages in the walk, in the order visitors see them. A page is in the walk today when it
          is Active or Test and today is within its dates. A visitor can still skip it if their
          email is on its suppression list or its pre-ping declines. Use Change order to put the
          pages in order. A new page joins the end of the walk on its own when you Schedule or Start
          it.
        </p>
        <p className="text-muted-foreground mt-2">
          Your pinned page shows first. The other pages follow by what each earned per response over
          the last 7 days. A response is a visitor clicking Accept or Skip on the page, and the
          ranking refreshes every night around 4 AM ET. A page with fewer than 100 responses in
          those 7 days is new: it keeps the place its spot number gives it until it has enough.
        </p>
        <p className="text-muted-foreground mt-2">
          Only the sequence visitors walk is shown; pages parked in a sequence nobody walks appear
          when their sequence joins the walk. Completed pages no longer hold a spot: a page moves to
          the Completed card the moment its campaign completes — end date, click limit or by hand —
          and its spot frees up right away.
        </p>
      </div>

      {data === null || sections === null ? (
        <p className="text-center text-muted-foreground py-8">Loading…</p>
      ) : (
        <>
          {!data.walkEnabled && (
            <Banner tone="stop" className="mb-6">
              The offer walk is switched off (OFFERS_WALK_ENABLED), so no visitor sees any custom
              page until it&apos;s on.
            </Banner>
          )}
          {data.walkOrder === 'spot' && (
            <Banner tone="warn" className="mb-6">
              {SPOT_MODE_BANNER}
            </Banner>
          )}
          {data.walkOrder === 'value' && (
            <Banner tone="note" className="mb-6">
              {VALUE_MODE_BANNER}
            </Banner>
          )}
          {/* Epic 115 D6: the Auto pin list card sits above the sequence
              sections — pending choices, never masquerading as the current
              pin. */}
          <AutoPinListCard autoPins={data.autoPins} />
          {/* HF-146 r2 (operator 2026-10-29): the one order section first, the
              Completed card second, Not placed after it. */}
          <GroupCard
            group={sections.order}
            data={data}
            editingSequence={editingSequence}
            pinBusy={pinBusy}
            onPin={(row) => void changePin(row, true)}
            onUnpin={(row) => void changePin(row, false)}
            onEdit={setEditingSequence}
            onSaved={(saved) => {
              setData(saved);
              setEditingSequence(null);
            }}
            onStale={() => {
              setEditingSequence(null);
              load();
            }}
            onCancel={() => setEditingSequence(null)}
          />
          {data.completedRows.length > 0 && <CompletedCard rows={data.completedRows} />}
          {sections.notPlaced !== null && (
            <GroupCard
              group={sections.notPlaced}
              data={data}
              editingSequence={editingSequence}
              pinBusy={pinBusy}
              onPin={(row) => void changePin(row, true)}
              onUnpin={(row) => void changePin(row, false)}
              onEdit={setEditingSequence}
              onSaved={(saved) => {
                setData(saved);
                setEditingSequence(null);
              }}
              onStale={() => {
                setEditingSequence(null);
                load();
              }}
              onCancel={() => setEditingSequence(null)}
            />
          )}
          {/* Chelsie 2026-10-28: the readout is a measurement summary, not a
              page group — it renders after the last group (Not placed). */}
          {data.valueStats !== null && <ReadoutCard valueStats={data.valueStats} />}
        </>
      )}
    </div>
  );
}
